import DairyService from '@src/api/services/dairy';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { StateEl } from '../style';
import { FormFields } from '@src/components/FormFields';
import { GlobalEl } from '@src/styles/app';
import DairyQuery from '@src/QueryHooks/DairyQuery';
import notify from '~/components/Notify';
import Drawer from '~/components/Drawer';
import { Button, Icon } from 'semantic-ui-react';
import { handleErrors } from '~/utils/helpers';
import Period from './Period';

interface CreateEditRestrictionPeriodsProps {
  isVisible: boolean;
  handleHiddenDrawer: () => void;
  isLoading?: boolean;
  fetchData?: () => void,
  idRestrictionPeriod?: any
}

const CreateEditRestrictionPeriods: React.FunctionComponent<CreateEditRestrictionPeriodsProps> = (props) => {
  const { isVisible, handleHiddenDrawer, idRestrictionPeriod } = props;
  const { t } = useTranslation();
  const [isBusy, setIsBusy] = useState(false);

  const methods = useForm({
    mode: 'onSubmit',
    defaultValues: {
      name: '',
      days: []
    }
  });

  const { handleSubmit, control, reset, formState } = methods

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'days'
  });

  useEffect(() => {
    if (idRestrictionPeriod) {
      editRestrictionPeriod(idRestrictionPeriod);
    } else {
      reset({
        name: '',
        days: []
      });
    }
  }, [isVisible, idRestrictionPeriod]);

  const onEditSuccess = (response) => {
    response.data.days.forEach(day => {
      day.timeFrom = day.timeFrom?.substring(0, 5) || ''
      day.timeTo = day.timeTo?.substring(0, 5) || ''
    })
    reset(response.data)
  }

  const { isLoading: isRestrictionPeriodLoading, mutateAsync: editRestrictionPeriod } =
    DairyQuery.editRestrictionPeriod(onEditSuccess);

  const onSubmit = async (data) => {
    setIsBusy(true);
    try {
      data.days.forEach(day => {
        day.maxTemp = day.maxTemp.toString().replace(',', '.')
      })
      if (!idRestrictionPeriod) {
        await DairyService.storeRestrictionPeriod(data);
        notify(t('Successfully created restriction period'), 'success');
      } else {
        await DairyService.updateRestrictionPeriod({ id: idRestrictionPeriod, data });
        notify(t('Successfully updated restriction period'), 'success');
      }
      props.fetchData();
      handleHiddenDrawer();
    } catch (errors) {
      handleErrors(errors);
    }
    setIsBusy(false);
  };

  const addDay = () => {
    append({
      day: null,
      timeFrom: '',
      timeTo: '',
      maxTemp: ''
    });
  };

  const renderDayFields = () => {
    return (
      <FormProvider {...methods}>
        {fields.map((field, index) => (
          <Period
            key={field.key}
            index={index}
            remove={remove}
          />
        ))}

        <Button
          type='button'
          icon
          labelPosition='left'
          color='blue'
          onClick={addDay}
          style={{ marginBottom: '20px' }}
        >
          <Icon name='plus' />
          {t('Add day')}
        </Button>
      </FormProvider>
    );
  };

  const renderContent = () => {
    return (
      <>
        <FormFields.Input
          control={control}
          name='name'
          label={t('Restriction period name')}
          placeholder={t('Restriction period name')}
          rules={{
            required: t('Please enter restriction period name'),
            maxLength: {
              value: 100,
              message: t('Max chart. is 100')
            }
          }}
        />
        {renderDayFields()}
        <StateEl.DrawerFooter>
          <GlobalEl.PrimaryButton
            disabled={Object.keys(formState.dirtyFields).length === 0}
          >{idRestrictionPeriod ? t('Update') : t('Add')}</GlobalEl.PrimaryButton>
        </StateEl.DrawerFooter>
      </>
    );
  };

  return <Drawer
    isVisible={isVisible}
    handleSubmit={handleSubmit(onSubmit)}
    minWidth={'500px'}
    isBusy={isRestrictionPeriodLoading || isBusy}
    header={idRestrictionPeriod ? t('Edit restriction period') : t('Create restriction period')}
    closeOnEscape
    onHide={() => {
      reset();
      handleHiddenDrawer();
    }}
    content={renderContent()}
  />
};

export default CreateEditRestrictionPeriods;
